/**
 * TablesManagement - Manage dining tables
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Grid3X3, Plus, Edit2, Trash2, Users, Check,
  Clock, Sparkles, AlertTriangle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  available: { color: 'bg-green-500', label: 'Available', icon: Check },
  occupied: { color: 'bg-blue-500', label: 'Occupied', icon: Users },
  reserved: { color: 'bg-yellow-500', label: 'Reserved', icon: Clock },
  cleaning: { color: 'bg-purple-500', label: 'Cleaning', icon: Sparkles }
};

const LOCATIONS = ['main', 'patio', 'private', 'bar', 'outdoor', 'vip'];

const TablesManagement = () => {
  const queryClient = useQueryClient();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingTable, setEditingTable] = useState(null);

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch tables
  const { data: tables = [], isLoading } = useQuery({
    queryKey: ['dining-tables'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/dining/tables`, { headers });
      if (!res.ok) return [];
      return res.json();
    },
    refetchInterval: 15000
  });

  // Create table
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/admin/dining/tables`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create table');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['dining-tables']);
      queryClient.invalidateQueries(['dining-stats']);
      setShowAddDialog(false);
      toast.success('Table created');
    },
    onError: (error) => toast.error(error.message)
  });

  // Update table
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_URL}/api/admin/dining/tables/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to update table');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['dining-tables']);
      setEditingTable(null);
      toast.success('Table updated');
    },
    onError: (error) => toast.error(error.message)
  });

  // Update table status
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }) => {
      const res = await fetch(`${API_URL}/api/admin/dining/tables/${id}/status`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ status })
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to update status');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['dining-tables']);
      queryClient.invalidateQueries(['dining-stats']);
    },
    onError: (error) => toast.error(error.message)
  });

  // Delete table
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/api/admin/dining/tables/${id}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to delete table');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['dining-tables']);
      queryClient.invalidateQueries(['dining-stats']);
      toast.success('Table deleted');
    },
    onError: (error) => toast.error(error.message)
  });

  const TableForm = ({ table, onSubmit, onCancel, isLoading }) => {
    const [formData, setFormData] = useState({
      table_number: table?.table_number || '',
      capacity: table?.capacity || 4,
      location: table?.location || 'main'
    });

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(formData);
        }}
        className="space-y-4"
      >
        <div>
          <label className="text-sm font-medium">Table Number *</label>
          <Input
            value={formData.table_number}
            onChange={(e) => setFormData({ ...formData, table_number: e.target.value })}
            required
            placeholder="e.g., T1, A1, 101"
            data-testid="input-table-number"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Capacity (seats)</label>
            <Input
              type="number"
              min="1"
              max="20"
              value={formData.capacity}
              onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 4 })}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Location</label>
            <Select
              value={formData.location}
              onValueChange={(value) => setFormData({ ...formData, location: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LOCATIONS.map((loc) => (
                  <SelectItem key={loc} value={loc} className="capitalize">
                    {loc}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={isLoading} data-testid="submit-table-btn">
            {isLoading ? 'Saving...' : table ? 'Update Table' : 'Create Table'}
          </Button>
        </div>
      </form>
    );
  };

  // Group tables by location
  const tablesByLocation = tables.reduce((acc, table) => {
    const loc = table.location || 'main';
    if (!acc[loc]) acc[loc] = [];
    acc[loc].push(table);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="tables-management">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Tables</h2>
          <p className="text-sm text-gray-500">Manage dining tables and seating</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-table-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Table
        </Button>
      </div>

      {/* Status Legend */}
      <div className="flex gap-4">
        {Object.entries(statusConfig).map(([status, config]) => (
          <div key={status} className="flex items-center gap-2 text-sm">
            <div className={`w-3 h-3 rounded-full ${config.color}`} />
            <span>{config.label}</span>
          </div>
        ))}
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading tables...</div>
      ) : tables.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Grid3X3 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium mb-2">No tables configured</h3>
            <p className="text-sm text-gray-500 mb-4">
              Add tables for dine-in orders
            </p>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add First Table
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(tablesByLocation).map(([location, locationTables]) => (
            <div key={location}>
              <h3 className="text-sm font-medium text-gray-500 mb-3 capitalize">{location}</h3>
              <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-8 gap-3">
                {locationTables.map((table) => {
                  const status = statusConfig[table.status] || statusConfig.available;
                  const StatusIcon = status.icon;
                  
                  return (
                    <Card 
                      key={table.id}
                      className={`cursor-pointer hover:shadow-md transition-shadow ${
                        table.status === 'occupied' ? 'ring-2 ring-blue-200' : ''
                      }`}
                      data-testid={`table-card-${table.id}`}
                    >
                      <CardContent className="p-3 text-center">
                        <div className={`w-full aspect-square rounded-lg ${status.color} text-white flex flex-col items-center justify-center mb-2`}>
                          <span className="text-2xl font-bold">{table.table_number}</span>
                          <span className="text-xs">{table.capacity} seats</span>
                        </div>
                        
                        {table.current_order && (
                          <div className="text-xs text-gray-500 mb-2">
                            <p>{table.current_order.order_number}</p>
                            <p className="font-medium">${table.current_order.total?.toFixed(2)}</p>
                          </div>
                        )}

                        {/* Quick status change */}
                        <div className="flex flex-wrap gap-1 justify-center">
                          {table.status !== 'available' && table.status !== 'occupied' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 text-xs"
                              onClick={() => updateStatusMutation.mutate({ id: table.id, status: 'available' })}
                            >
                              ✓ Ready
                            </Button>
                          )}
                          {table.status === 'occupied' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 text-xs"
                              onClick={() => updateStatusMutation.mutate({ id: table.id, status: 'cleaning' })}
                            >
                              🧹 Clean
                            </Button>
                          )}
                          {table.status === 'available' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 text-xs"
                              onClick={() => updateStatusMutation.mutate({ id: table.id, status: 'reserved' })}
                            >
                              📅 Reserve
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 text-xs"
                            onClick={() => setEditingTable(table)}
                          >
                            <Edit2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Table Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Table</DialogTitle>
          </DialogHeader>
          <TableForm
            onSubmit={(data) => createMutation.mutate(data)}
            onCancel={() => setShowAddDialog(false)}
            isLoading={createMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Table Dialog */}
      <Dialog open={!!editingTable} onOpenChange={(open) => !open && setEditingTable(null)}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Table {editingTable?.table_number}</DialogTitle>
          </DialogHeader>
          {editingTable && (
            <>
              <TableForm
                table={editingTable}
                onSubmit={(data) => updateMutation.mutate({ id: editingTable.id, data })}
                onCancel={() => setEditingTable(null)}
                isLoading={updateMutation.isPending}
              />
              {editingTable.status !== 'occupied' && (
                <Button
                  variant="destructive"
                  size="sm"
                  className="w-full mt-2"
                  onClick={() => {
                    if (confirm('Delete this table?')) {
                      deleteMutation.mutate(editingTable.id);
                      setEditingTable(null);
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Table
                </Button>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TablesManagement;
